import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import "./TradingOverlay.styles.scss";

// Type Imports
import { IAlertMessage } from "@pulse-ui/types/components/AlertMessage";
import { ISelectOption } from "@pulse-ui/types/components/DropdownInput";
import { IBookEntries } from "@pulse-ui/codegen/generated_ts/market_data";
import { ContextStateType } from "@/context/context.types";
import {
  IAccountMetadata,
  IPortfolioMetadata,
} from "@pulse-ui/codegen/generated_ts/org_metadata";
import {
  IExecutionReport,
  IOrderRestatement,
} from "@pulse-ui/codegen/generated_ts/drop_copy";
import {
  BookSide,
  MdEntryType,
  OrderStatus,
} from "@pulse-ui/codegen/generated_ts/types";
import { IAGOrderBookDataRow } from "@pulse-ui/types/components/AGTable";

// Component Imports
import { AgGridReact } from "ag-grid-react";
import { AlertMessage } from "../../components";
import { Loading } from "../../layout";
import AGOrderBook from "@/components/AGOrderBook/AGOrderBook";
import { AutocompleteInput, DropdownInput } from "@/components/Inputs";

// Material UI Imports
import { SvgIcon } from "@mui/material";

// Package Imports
import { v4 as uuidv4 } from "uuid";

// Context
import { useAppContext } from "../../context/context";

// SVG Imports
import LogoSVG from "@/svgs/pulse_prime_logo.svg?react";

// Utils/Promise/Helper Function Imports
import {
  getApiKeys,
  getEnabledVenueInstruments,
  getOrgMetadata,
  getOrgs,
  setAccountById,
  setOrgMetadata,
  setPortfolioById,
  startBookOverrideForSymbol,
  stopBookOverrideForSymbol,
} from "../../utils/promises";
import { useAlert } from "@/utils/hooks/useAlert/useAlert";
import { grab_base_url } from "@/utils/api_methods";
import { usePortfolioAndAccountSelection } from "@/utils/hooks/usePortfolioAndAccountSelection/usePortfolioAndAccountSelection";
import dayjs from "dayjs";

// Key: price, Value: IBookEntries
export type BookMap = Map<string, IBookEntries>;

// Map<String, Number> Key: client_order_id, Value: quantity
export type OrderLevel = {
  symbol: string;
  asks: Map<string, number>;
  bids: Map<string, number>;
  has_mktdata_row: boolean;
  side: MdEntryType;
};

// Key: price, Value: { asks: Map<string, number>, bids: Map<string, number>, has_mktdata_row: boolean }
export type OrderMap = Map<string, OrderLevel>;

export type OrderBookLevel = {
  bids_dollar_value: string;
  bid_mktdata_qty: string;
  bid_dropcopy_qtys: string;
  ask_mktdata_qty: string;
  ask_dropcopy_qtys: string;
  asks_dollar_value: string;
  type: MdEntryType;
};

// Key: price, Value: { bid_cloids: string, bid_qtys: string, ask_cloids: string, ask_qtys: string }
export type UnifiedOrderBookMap = Map<string, OrderBookLevel>;

const BOOK = "book";
const ORDER = "order";
const ALL = "all";
const SET = "set";
const DELETE = "delete";
const CHANGE = "change";
const ASK = "ask";
const BID = "bid";
const EXECUTION_REPORT = "execution_report";
const NEW = "new";
const BOOK_RESET = "book_reset";

const TradingOverlay = () => {
  // State - START -------------------------------------
  // Query Params States
  const [queryParams, setQueryParams] = useSearchParams({
    accounts: "",
  });
  const location_origin = window.location.origin;
  const accounts_param = queryParams.get("accounts");
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact<IAGOrderBookDataRow> | null>(null);
  const [bookData, setBookData] = useState<BookMap>(new Map());
  const [orderData, setOrderData] = useState<OrderMap>(new Map());
  const [topAsks, setTopAsks] = useState<UnifiedOrderBookMap>(new Map());
  const [topBids, setTopBids] = useState<UnifiedOrderBookMap>(new Map());
  const [rowData, setRowData] = useState<IAGOrderBookDataRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [openOrdersReady, setOpenOrdersReady] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isConnectingOpenOrders, setIsConnectingOpenOrders] =
    useState<boolean>(false);
  const [isConnectingBookFeed, setIsConnectingBookFeed] =
    useState<boolean>(false);
  const book_socketRef = useRef<WebSocket | null>(null);
  const open_orders_socketRef = useRef<WebSocket | null>(null);
  const [symbolOptions, setSymbolOptions] = useState<ISelectOption[]>([]);

  // Request cancellation and connection tracking
  const [currentBookOverrideRequest, setCurrentBookOverrideRequest] =
    useState<AbortController | null>(null);
  const [currentSymbol, setCurrentSymbol] = useState<string | null>(null);
  const [isBookOverrideActive, setIsBookOverrideActive] =
    useState<boolean>(false);
  const [currentVenue, setCurrentVenue] = useState<string | null>(null);
  const [orderLiveTimestamp, setOrderLiveTimestamp] = useState<string | null>(
    null
  );
  const [bookLiveTimestamp, setBookLiveTimestamp] = useState<string | null>(
    null
  );

  // Error Handling / Alert Message States
  const { alerts, pushAlert, closeAlert, clearAlerts } = useAlert();

  // Context States
  const {
    context_state,
    update_org_metadata,
    set_portfolio_by_id,
    set_account_by_id,
    set_symbol,
  } = useAppContext();

  const stringified_local_metadata: string | null =
    localStorage.getItem("pulse-ctx");
  const local_metadata: ContextStateType | null = stringified_local_metadata
    ? JSON.parse(stringified_local_metadata)
    : null;

  const memoizedSymbolOptions = useMemo(() => symbolOptions, [symbolOptions]);
  const symbolValue = useMemo(
    () => ({
      label: context_state.selected_symbol || "",
      value: context_state.selected_symbol || "",
    }),
    [context_state.selected_symbol]
  );
  const bookFeedRetryCountRef = useRef<number>(0);
  const currentSymbolRef = useRef<string | null>(null);
  // State - END ---------------------------------------

  // Add this helper function near the top of the component
  const normalizePrice = (price: any): string => {
    return parseFloat(price?.toString() || "0").toString();
  };

  // Methods - START -----------------------------------
  const handleNavigate = (account_id: string | null) => {
    setQueryParams((prev: URLSearchParams) => {
      prev.set("accounts", account_id || "");
      return prev;
    });
  };

  const mapIPortfoliosToSelectOptions = (
    portfolios: IPortfolioMetadata[]
  ): ISelectOption[] => {
    if (!portfolios) {
      return [];
    } else {
      let options: ISelectOption[] = [];

      for (let portfolio of portfolios) {
        options.push({
          label: portfolio.display_name,
          value: portfolio.portfolio_id,
        });
      }

      return options;
    }
  };

  const mapIAccountsToSelectOptions = (
    accounts: IAccountMetadata[]
  ): ISelectOption[] => {
    if (!accounts) {
      return [];
    } else {
      let options: ISelectOption[] = [];

      for (let account of accounts) {
        options.push({
          label: account.display_name,
          value: account.account_id,
        });
      }

      return options;
    }
  };

  const cancelCurrentBookOverride = async () => {
    if (currentBookOverrideRequest) {
      currentBookOverrideRequest.abort();
      setCurrentBookOverrideRequest(null);
    }

    if (isBookOverrideActive && currentSymbol) {
      try {
        await stopBookOverrideForSymbol(location_origin, currentSymbol);
      } catch (error) {
        pushAlert({
          id: uuidv4(),
          message:
            "Failed to stop previous book override during cancellation. This is not a critical error but you may see some inconsistencies in the book data.",
          status: 400,
        });
      }
      setIsBookOverrideActive(false);
    }
  };

  const clearAllData = () => {
    setBookData(new Map());
    setOrderData(new Map());
    setRowData([]);
    setOrderLiveTimestamp(null);
    setBookLiveTimestamp(null);
  };

  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (alerts.length > 0) clearAlerts();

    // Clear existing connections
    disconnectFromWebsocket(ALL);
    setOpenOrdersReady(false);

    // Explicitly stop book override if active
    if (isBookOverrideActive && currentSymbol) {
      stopBookOverrideForSymbol(location_origin, currentSymbol).catch(
        (error: any) => {
          console.warn("Failed to stop book override:", error);
        }
      );
      setIsBookOverrideActive(false);
    }

    // Clear symbol-related state when portfolio/account changes
    set_symbol(null);
    setCurrentSymbol(null);
    setSymbolOptions([]);
    clearAllData();

    if (name === "portfolio-select") {
      // Update portfolio and account
      value === "" ? set_portfolio_by_id(null) : set_portfolio_by_id(value);
      set_account_by_id(null);
      setCurrentVenue(null);
      handleNavigate(null);
      return;
    }

    if (name === "account-select") {
      value === "" ? set_account_by_id(null) : set_account_by_id(value);
      setCurrentVenue(null);
      handleNavigate(value || null);
    }
  };

  const handleSymbolAutocompleteDropdownChange = async (
    _: React.SyntheticEvent,
    newValue: ISelectOption | null
  ) => {
    // Cancel any in-flight book override request
    await cancelCurrentBookOverride();

    // Clear current data maps
    clearAllData();

    if (newValue) {
      setCurrentSymbol(newValue.value);
      set_symbol(newValue.value);
    } else {
      set_symbol(null);
    }
  };

  const handleInitialDataLoad = async () => {
    setLoading(true);

    try {
      // Get org data
      const get_orgs_res = await getOrgs(location_origin);
      const get_org_metadata_res = await getOrgMetadata(
        get_orgs_res.data.org_id,
        location_origin
      );
      await setOrgMetadata(get_org_metadata_res.data, update_org_metadata);

      // Handle portfolio/account selection
      const { portfolio_id_res, account_id_res } =
        await usePortfolioAndAccountSelection(
          accounts_param,
          local_metadata,
          get_org_metadata_res.data,
          true
        );

      await setPortfolioById(portfolio_id_res || null, set_portfolio_by_id);
      await setAccountById(account_id_res, set_account_by_id);

      // Fetch venue instruments if we have an account
      if (account_id_res) {
        await handleFetchVenueInstruments(account_id_res);
      }

      // Handle navigation
      handleNavigate(account_id_res || null);

      // Validate access
      if (!portfolio_id_res) {
        set_account_by_id(null);
        set_portfolio_by_id(null);
        navigate("/unauthorized", { replace: true });
        return;
      }

      setIsInitialized(true);
    } catch (error_res: any) {
      if (error_res?.status === 408) {
        pushAlert({
          id: uuidv4(),
          message: error_res?.message || "Request timeout",
          status: 408,
          onClickText: "Reload page?",
          onClickMethod: () => window.location.reload(),
        });
      } else {
        pushAlert({
          id: uuidv4(),
          message:
            error_res?.message ||
            "An error occurred establishing initial connections.",
          status: error_res?.status || 500,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // New method to handle open orders socket connection
  const handleOpenOrdersConnection = async (account_id: string) => {
    if (!account_id || isConnectingOpenOrders) return;

    setIsConnectingOpenOrders(true);
    try {
      // Disconnect existing socket first
      disconnectFromWebsocket(ORDER);
      setOpenOrdersReady(false);

      // Connect to new socket
      await connectToOpenOrdersWebsocket(account_id);
      setOpenOrdersReady(true);
    } catch (error: any) {
      console.error("Failed to connect to open orders socket:", error);
      pushAlert({
        id: uuidv4(),
        message: "Failed to connect to open orders feed",
        status: error?.status || 500,
      });
    } finally {
      setIsConnectingOpenOrders(false);
    }
  };

  const handleBookFeedConnection = async (symbol: string) => {
    // Don't connect if symbol is null/empty or if we're clearing
    if (!symbol || isConnectingBookFeed || symbol === "") {
      return;
    }

    // Don't connect if the context symbol is null (being cleared)
    if (context_state.selected_symbol === null) {
      return;
    }

    setIsConnectingBookFeed(true);

    try {
      // Stop book override for previous symbol if different
      if (isBookOverrideActive && currentSymbol && currentSymbol !== symbol) {
        try {
          await stopBookOverrideForSymbol(location_origin, currentSymbol);
        } catch (error) {
          pushAlert({
            id: uuidv4(),
            message: `Failed to stop book override for symbol: ${currentSymbol}. ${
              error && (error as any).message
                ? `Error: ${(error as any).message}`
                : ""
            }`,
            status: 500,
          });
        }
        setIsBookOverrideActive(false);
      }

      // Disconnect existing socket
      disconnectFromWebsocket(BOOK);

      // Create new abort controller for this request
      const abortController = new AbortController();
      setCurrentBookOverrideRequest(abortController);

      // Start book override first, then connect to socket once it's successful
      await startBookOverrideForSymbol(location_origin, symbol);

      // Check if request was cancelled
      if (abortController.signal.aborted) {
        return;
      }

      setIsBookOverrideActive(true);
      setCurrentBookOverrideRequest(null);

      // Now connect to the WebSocket after override is successful
      await connectToBookFeedWebsocket(symbol);
    } catch (error: any) {
      // Only show error if this wasn't a cancellation
      if (error?.name !== "AbortError") {
        pushAlert({
          id: uuidv4(),
          message: "Failed to connect to book feed",
          status: error?.status || 500,
        });
      }
    } finally {
      setIsConnectingBookFeed(false);
      setCurrentBookOverrideRequest(null);
    }
  };

  const handleFetchVenueInstruments = async (account_id: string) => {
    const get_api_keys_res = await getApiKeys(location_origin, account_id);

    let venue_codes: Set<string> = new Set();

    if (
      get_api_keys_res.data &&
      get_api_keys_res.data.keys &&
      get_api_keys_res.data.keys.length > 0
    ) {
      for (let api_key of get_api_keys_res.data.keys) {
        venue_codes.add(api_key.key.venue);
      }
    }

    if (venue_codes.size === 0) {
      pushAlert({
        id: uuidv4(),
        message: "No API Keys found for this account.",
        status: 400,
      });
    } else if (venue_codes.size > 1) {
      pushAlert({
        id: uuidv4(),
        message:
          "Multiple API Keys found for this account. Listing available symbols for first API Key.",
        status: 300,
      });
    } else {
      const venue_code = Array.from(venue_codes)[0];
      setCurrentVenue(venue_code);
      const get_venue_instruments_res = await getEnabledVenueInstruments(
        location_origin,
        venue_code,
        true,
        true
      );

      if (
        get_venue_instruments_res.status === 200 &&
        get_venue_instruments_res.data &&
        get_venue_instruments_res.data.values
      ) {
        const symbol_options: ISelectOption[] =
          get_venue_instruments_res.data.map((symbol: string) => {
            return {
              label: symbol,
              value: symbol,
            };
          });
        setSymbolOptions(symbol_options);

        if (symbol_options.length > 0) {
          const firstSymbol = symbol_options[0].value;
          set_symbol(firstSymbol);
          setCurrentSymbol(firstSymbol);

          if (isInitialized) {
            handleBookFeedConnection(firstSymbol);
          }
        }
      } else {
        if (symbolOptions.length > 0) {
          setSymbolOptions([]);
        }

        pushAlert({
          id: uuidv4(),
          message: "No symbols found for this venue.",
          status: 400,
        });
      }
    }
  };

  const isOrderRestatement = (
    row: IOrderRestatement | IExecutionReport
  ): row is IOrderRestatement => {
    return (row as IOrderRestatement).orders_snapshot !== undefined;
  };

  const getExecutionReportAction = (execution_report: IExecutionReport) => {
    const statusKey =
      execution_report.status as unknown as keyof typeof OrderStatus;
    if (
      OrderStatus[statusKey] === OrderStatus.canceled ||
      OrderStatus[statusKey] === OrderStatus.expired ||
      OrderStatus[statusKey] === OrderStatus.filled ||
      OrderStatus[statusKey] === OrderStatus.rejected
    ) {
      return DELETE;
    } else {
      return SET;
    }
  };

  const connectToOpenOrdersWebsocket = (
    accounts: string
  ): Promise<{
    status: number;
    error: any;
  }> => {
    return new Promise((resolve, reject) => {
      const jwtToken = localStorage.getItem("pulsejwt");

      const websocket_url = `${grab_base_url(
        location_origin,
        "wss"
      )}/api/dropcopy/v1/order_event?accounts=${accounts}`;

      if (jwtToken && open_orders_socketRef.current === null) {
        open_orders_socketRef.current = new WebSocket(websocket_url, [
          jwtToken,
        ]);

        let messageReceived = false;
        let messageTimeout: any;

        const connectionTimeout = setTimeout(() => {
          open_orders_socketRef.current?.close();
          open_orders_socketRef.current = null;
          reject({
            status: 408,
            data: null,
            error: "Connection timed out",
          });
        }, 5000);

        open_orders_socketRef.current.onopen = () => {
          clearTimeout(connectionTimeout);

          messageTimeout = setTimeout(() => {
            if (!messageReceived) {
              open_orders_socketRef.current?.close();
              open_orders_socketRef.current = null;
              reject({
                status: 408,
                data: null,
                error: "No messages received",
              });
            }
          }, 30000);

          if (!openOrdersReady) {
            setOpenOrdersReady(true);
          }

          resolve({
            status: 200,
            error: null,
          });
        };

        open_orders_socketRef.current.onmessage = (event: any): void => {
          const parsedEvent = JSON.parse(event.data);

          setOrderLiveTimestamp(parsedEvent.transact_time);

          messageReceived = true;
          clearTimeout(messageTimeout);

          if (isOrderRestatement(parsedEvent)) {
            const open_orders_data = parsedEvent.orders_snapshot;

            // Check if there are any orders in the snapshot
            if (open_orders_data && open_orders_data.length > 0) {
              for (let item of open_orders_data) {
                // Determine if this order should be added (SET) or removed (DELETE) based on its status
                const action = getExecutionReportAction(item);

                // Update the order data with this order
                setOrderData((prev) => {
                  // Create a new Map instance to ensure React detects the state change
                  let temp_map = new Map(prev);

                  // Extract the price as a string, defaulting to empty string if null
                  const price = normalizePrice(item.price?.toString() || "");

                  // Convert the order side (bid/ask) to the corresponding BookSide enum key
                  const side = item.side as unknown as keyof typeof BookSide;

                  // Get existing entry for this price level, or create a new one with empty arrays
                  const current_entry = temp_map.get(price) || {
                    symbol: item.symbol,
                    asks: new Map(),
                    bids: new Map(),
                    has_mktdata_row: false, // This will be updated by the unified order book logic
                    side: item.side as unknown as MdEntryType,
                  };

                  if (action === SET) {
                    // Set entry for this price level on this side, creates new one if it doesn't exist
                    current_entry[`${side}s`].set(
                      item.client_order_id,
                      item.order_qty || 0
                    );
                    temp_map.set(price, current_entry);
                  } else {
                    // Remove from existing entry
                    current_entry[`${side}s`].delete(item.client_order_id);

                    // Check if both sides are empty after removal
                    if (
                      current_entry.asks.size === 0 &&
                      current_entry.bids.size === 0
                    ) {
                      temp_map.delete(price);
                    } else {
                      temp_map.set(price, current_entry);
                    }
                  }

                  return temp_map;
                });
              }
            }
          } else {
            if (parsedEvent.event_type === EXECUTION_REPORT) {
              // Determine if this order should be added (SET) or removed (DELETE) based on its status
              const action = getExecutionReportAction(parsedEvent);

              // Update the order data with this order
              setOrderData((prev) => {
                // Create a new Map instance to ensure React detects the state change
                let temp_map = new Map(prev);

                // Extract the price as a string, defaulting to empty string if null
                const price = normalizePrice(
                  parsedEvent.price?.toString() || ""
                );

                // Convert the order side (bid/ask) to the corresponding BookSide enum key
                const side =
                  parsedEvent.side as unknown as keyof typeof BookSide;

                // Get existing entry for this price level, or create a new one
                const current_entry = temp_map.get(price) || {
                  symbol: parsedEvent.symbol,
                  asks: new Map(),
                  bids: new Map(),
                  has_mktdata_row: false, // This will be updated by the unified order book logic
                  side: parsedEvent.side as unknown as MdEntryType,
                };

                if (action === SET) {
                  // Set entry for this price level on this side, creates new one if it doesn't exist
                  current_entry[`${side}s`].set(
                    parsedEvent.client_order_id,
                    parsedEvent.order_qty || 0
                  );
                  temp_map.set(price, current_entry);
                } else {
                  // Remove from existing entry
                  current_entry[`${side}s`].delete(parsedEvent.client_order_id);

                  // Check if both sides are empty after removal
                  if (
                    current_entry.asks.size === 0 &&
                    current_entry.bids.size === 0
                  ) {
                    // Use the same price key that was used when creating the entry
                    const priceKey = normalizePrice(parsedEvent.price);
                    temp_map.delete(priceKey);
                  } else {
                    temp_map.set(price, current_entry);
                  }
                }

                return temp_map;
              });
            }
          }
        };

        open_orders_socketRef.current.onerror = (event: any) => {
          reject({
            status: event.code,
            data: null,
            error: event,
          });
        };
      } else {
        if (!jwtToken) {
          reject({
            status: 401,
            data: null,
            error:
              "Your session has expired. If you're not automatically redirected, please navigate to the home page.",
          });
        }
      }
    });
  };

  const connectToBookFeedWebsocket = (
    symbol: string
  ): Promise<{
    status: number;
    error: any;
  }> => {
    return new Promise(async (resolve, reject) => {
      const jwtToken = localStorage.getItem("pulsejwt");
      const websocket_url = `${grab_base_url(
        location_origin,
        "wss"
      )}/api/mktdata/v1/book/${symbol}`;

      if (jwtToken) {
        if (book_socketRef.current === null) {
          book_socketRef.current = new WebSocket(websocket_url, [jwtToken]);

          let messageReceived = false;
          let messageTimeout: any;

          const connectionTimeout = setTimeout(() => {
            book_socketRef.current?.close();
            book_socketRef.current = null;
            reject({
              status: 408,
              error: "Connection timed out",
            });
          }, 5000);

          book_socketRef.current.onopen = async () => {
            try {
              clearTimeout(connectionTimeout);

              messageTimeout = setTimeout(() => {
                if (!messageReceived) {
                  book_socketRef.current?.close();
                  book_socketRef.current = null;
                  reject({
                    status: 408,
                    data: null,
                    error: "No messages received",
                  });
                }
              }, 5000);

              resolve({ status: 200, error: null });
            } catch (error: any) {
              reject({ status: 500, error });
            }
          };

          book_socketRef.current.onmessage = (event: any): void => {
            // Parse the incoming WebSocket message
            const parsedEvent = JSON.parse(event.data);

            // Mark that we've received a message and clear the timeout
            messageReceived = true;
            clearTimeout(messageTimeout);

            setBookLiveTimestamp(parsedEvent.sent);

            // Extract book entries from the parsed event
            const book_entries = parsedEvent.entries;

            // Check if there are any book entries to process
            if (book_entries && book_entries.length > 0) {
              // Iterate through each book entry
              for (let item of book_entries) {
                // Only process ask or bid entries
                if (item.type === ASK || item.type === BID) {
                  setBookData((prev) => {
                    // Create a new Map instance to ensure React detects the state change
                    let temp_map: Map<string, IBookEntries> = new Map(prev);

                    if (item.action === NEW || item.action === CHANGE) {
                      // Add the new book entry to the book data map
                      temp_map.set(normalizePrice(item.price), item);
                    } else {
                      // Remove the book entry from the book data map
                      temp_map.delete(normalizePrice(item.price));
                    }

                    return temp_map;
                  });
                } else if (item.type === BOOK_RESET) {
                  setBookData(new Map());
                }
              }
            }
          };

          book_socketRef.current.onerror = (event: any) => {
            reject({
              status: event.code,
              data: null,
              error: event,
            });
          };

          book_socketRef.current.onclose = (event: any) => {
            const wasActive = book_socketRef.current !== null;
            book_socketRef.current = null;

            if (
              wasActive &&
              !event.wasClean &&
              bookFeedRetryCountRef.current < 30 &&
              currentSymbolRef.current &&
              currentSymbolRef.current === symbol
            ) {
              bookFeedRetryCountRef.current++;
              setTimeout(() => {
                connectToBookFeedWebsocket(symbol);
              }, 1000);
            } else {
              // Only show error alert if we've exhausted retries due to actual connection failure
              // Don't show alert for intentional disconnections (symbol changes/clearing)
              if (bookFeedRetryCountRef.current >= 30 && wasActive) {
                pushAlert({
                  id: uuidv4(),
                  message: "Market data connection failed.",
                  status: 500,
                });
              }

              bookFeedRetryCountRef.current = 0;
            }
          };
        }
      } else {
        reject({
          status: 401,
          data: null,
          error:
            "Your session has expired. If you're not automatically redirected, please navigate to the home page.",
        });
      }
    });
  };

  const disconnectFromWebsocket = (
    type: typeof BOOK | typeof ORDER | typeof ALL
  ) => {
    if (type === BOOK || type === ALL) {
      // Cancel any pending book override request
      if (currentBookOverrideRequest) {
        currentBookOverrideRequest.abort();
        setCurrentBookOverrideRequest(null);
      }

      // Stop book override for current symbol (not previousSymbol)
      if (isBookOverrideActive && currentSymbol) {
        stopBookOverrideForSymbol(location_origin, currentSymbol).catch(
          (error: any) => {
            pushAlert({
              id: uuidv4(),
              message: `Failed to stop book override for symbol: ${currentSymbol}. ${
                error && error.message ? `Error: ${error.message}` : ""
              }`,
              status: 500,
            });
          }
        );
        setIsBookOverrideActive(false);
      }

      // Reset retry count before closing to prevent reconnection on intentional disconnect
      bookFeedRetryCountRef.current = 0;

      if (book_socketRef.current) {
        if (
          book_socketRef.current.readyState === 1 ||
          book_socketRef.current.readyState === 3
        ) {
          book_socketRef.current.close();
          book_socketRef.current = null;
        }
      }
    }

    if (type === ORDER || type === ALL) {
      if (open_orders_socketRef.current) {
        if (
          open_orders_socketRef.current.readyState === 1 ||
          open_orders_socketRef.current.readyState === 3
        ) {
          open_orders_socketRef.current.close();
          open_orders_socketRef.current = null;
        }
      }
    }
  };

  const updateTopArrays = useCallback(() => {
    // Get all book entries and filter by type
    const askEntries = Array.from(bookData.values())
      .filter((entry) => entry.type === (ASK as any))
      .sort(
        (a, b) =>
          parseFloat(a.price?.toString() || "0") -
          parseFloat(b.price?.toString() || "0")
      );

    const bidEntries = Array.from(bookData.values())
      .filter((entry) => entry.type === (BID as any))
      .sort(
        (a, b) =>
          parseFloat(b.price?.toString() || "0") -
          parseFloat(a.price?.toString() || "0")
      );

    // Take top 15 asks
    const newTopAsks = new Map<string, OrderBookLevel>();
    askEntries.slice(0, 15).forEach((ask) => {
      const price = normalizePrice(ask.price);
      const orderEntry = orderData.get(price);

      // Calculate total ask quantities from orders
      let totalAskQty = 0;
      if (orderEntry?.asks) {
        for (const qty of orderEntry.asks.values()) {
          totalAskQty += qty;
        }
      }

      newTopAsks.set(price, {
        bids_dollar_value: "0.00",
        bid_dropcopy_qtys: "",
        bid_mktdata_qty: "0",
        ask_dropcopy_qtys: totalAskQty.toString(),
        ask_mktdata_qty: ask.quantity?.toString() || "0",
        asks_dollar_value: getDollarValue(totalAskQty, parseFloat(price)),
        type: ask.type,
      });
    });

    // Take top 15 bids
    const newTopBids = new Map<string, OrderBookLevel>();
    bidEntries.slice(0, 15).forEach((bid) => {
      const price = normalizePrice(bid.price);
      const orderEntry = orderData.get(price);

      // Calculate total bid quantities from orders
      let totalBidQty = 0;
      if (orderEntry?.bids) {
        for (const qty of orderEntry.bids.values()) {
          totalBidQty += qty;
        }
      }

      newTopBids.set(price, {
        bids_dollar_value: getDollarValue(totalBidQty, parseFloat(price)),
        bid_dropcopy_qtys: totalBidQty.toString(),
        bid_mktdata_qty: bid.quantity?.toString() || "0",
        ask_dropcopy_qtys: "",
        ask_mktdata_qty: "0",
        asks_dollar_value: "0.00",
        type: bid.type,
      });
    });

    // Pad asks to exactly 15 rows
    while (newTopAsks.size < 15) {
      const padKey = `pad_${newTopAsks.size}`;
      newTopAsks.set(padKey, {
        bids_dollar_value: "0.00",
        bid_dropcopy_qtys: "",
        bid_mktdata_qty: "0",
        ask_dropcopy_qtys: "",
        ask_mktdata_qty: "0",
        asks_dollar_value: "0.00",
        type: MdEntryType.ask,
      });
    }

    // Pad bids to exactly 15 rows
    while (newTopBids.size < 15) {
      const padKey = `pad_${newTopBids.size}`;
      newTopBids.set(padKey, {
        bids_dollar_value: "0.00",
        bid_dropcopy_qtys: "",
        bid_mktdata_qty: "0",
        ask_dropcopy_qtys: "",
        ask_mktdata_qty: "0",
        asks_dollar_value: "0.00",
        type: MdEntryType.bid,
      });
    }

    setTopAsks(newTopAsks);
    setTopBids(newTopBids);
  }, [bookData, orderData, context_state.selected_symbol]);

  const parseUnifiedOrderBook = () => {
    let temp_data: IAGOrderBookDataRow[] = [];

    // Convert topAsks Map to array and sort
    const askEntries = Array.from(topAsks.entries())
      .filter(([key, _]) => !key.startsWith("pad_"))
      .sort(([a], [b]) => parseFloat(b) - parseFloat(a));

    // Convert topBids Map to array and sort
    const bidEntries = Array.from(topBids.entries())
      .filter(([key, _]) => !key.startsWith("pad_"))
      .sort(([a], [b]) => parseFloat(b) - parseFloat(a));

    // Process asks first (highest prices at top)
    askEntries.forEach(([price, entry]) => {
      temp_data.push({
        bids_dollar_value: entry.bids_dollar_value,
        bid_dropcopy_qtys: entry.bid_dropcopy_qtys,
        bid_mktdata_qty: entry.bid_mktdata_qty,
        price,
        ask_mktdata_qty: entry.ask_mktdata_qty,
        ask_dropcopy_qtys: entry.ask_dropcopy_qtys,
        asks_dollar_value: entry.asks_dollar_value,
        side: entry.type,
      } as IAGOrderBookDataRow);
    });

    // Process bids (highest prices first)
    bidEntries.forEach(([price, entry]) => {
      temp_data.push({
        bids_dollar_value: entry.bids_dollar_value,
        bid_dropcopy_qtys: entry.bid_dropcopy_qtys,
        bid_mktdata_qty: entry.bid_mktdata_qty,
        price,
        ask_mktdata_qty: entry.ask_mktdata_qty,
        ask_dropcopy_qtys: entry.ask_dropcopy_qtys,
        asks_dollar_value: entry.asks_dollar_value,
        side: entry.type,
      } as IAGOrderBookDataRow);
    });

    setRowData(temp_data);
  };

  const getDollarValue = (qty: number, price: number) => {
    return (qty * price).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const cleanup = () => {
    cancelCurrentBookOverride();
    disconnectFromWebsocket(ALL);
    setOpenOrdersReady(false);
    setIsConnectingOpenOrders(false);
    setIsConnectingBookFeed(false);
    setIsBookOverrideActive(false);
    setCurrentBookOverrideRequest(null);
    setCurrentSymbol(null);
  };
  // Methods - END -------------------------------------

  // useEffects - START --------------------------------
  useEffect(() => {
    handleInitialDataLoad();

    return cleanup;
  }, []);

  // Handle account changes (including initial load)
  useEffect(() => {
    if (!isInitialized) return;

    const account_id = context_state?.selected_account?.account_id;

    if (account_id) {
      // Clear existing order data when switching accounts
      setOrderData(new Map());

      handleOpenOrdersConnection(account_id);
      handleNavigate(account_id);

      // Check if we need to fetch venue instruments
      const checkAndFetchVenueInstruments = async () => {
        try {
          const get_api_keys_res = await getApiKeys(
            location_origin,
            account_id
          );

          if (
            get_api_keys_res.data &&
            get_api_keys_res.data.keys &&
            get_api_keys_res.data.keys.length > 0
          ) {
            // Get the venue from the first API key
            const newVenue = get_api_keys_res.data.keys[0].key.venue;

            // Only fetch venue instruments if the venue has changed
            if (newVenue !== currentVenue) {
              setCurrentVenue(newVenue);
              await handleFetchVenueInstruments(account_id);
            }
          }
        } catch (err) {
          pushAlert({
            id: uuidv4(),
            message: `Failed to fetch api keys.`,
            status: 500,
          });
        }
      };

      // Call the venue check function
      checkAndFetchVenueInstruments();
    } else {
      disconnectFromWebsocket(ORDER);
      setOpenOrdersReady(false);
      // Clear order data when no account is selected
      setOrderData(new Map());
      setCurrentVenue(null);
      handleNavigate(null);
    }
  }, [isInitialized, context_state.selected_account]);

  // Handle symbol changes
  useEffect(() => {
    if (!isInitialized) return;

    const symbol = context_state?.selected_symbol;

    if (symbol) {
      // Clear existing order data when switching symbols
      setOrderData(new Map());

      // Update currentSymbol if it's different
      if (symbol !== currentSymbol) {
        setCurrentSymbol(symbol);
      }

      handleBookFeedConnection(symbol);
      handleNavigate(context_state.selected_account?.account_id || null);
    } else {
      // Symbol was cleared - stop book override for current symbol before disconnecting
      if (isBookOverrideActive && currentSymbol) {
        stopBookOverrideForSymbol(location_origin, currentSymbol).catch(
          (error: any) => {
            pushAlert({
              id: uuidv4(),
              message: `Failed to stop book override for symbol: ${currentSymbol}. ${
                error && error.message ? `Error: ${error.message}` : ""
              }`,
              status: 500,
            });
          }
        );
        setIsBookOverrideActive(false);
      }

      disconnectFromWebsocket(BOOK);
      // Clear order data when no symbol is selected
      setOrderData(new Map());
      setCurrentSymbol(null);
      handleNavigate(context_state.selected_account?.account_id || null);
    }
  }, [isInitialized, context_state.selected_symbol]);

  useEffect(() => {
    parseUnifiedOrderBook();
  }, [topAsks, topBids]);

  useEffect(() => {
    updateTopArrays();
  }, [topAsks, topBids]);

  useEffect(() => {
    currentSymbolRef.current = currentSymbol;
  }, [currentSymbol]);
  // useEffects - END ----------------------------------

  return (
    <div className="pulse-sidenav-adjusted-container pulse-bg-color--background-900">
      <SvgIcon
        sx={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
        children={<LogoSVG className="pulse-color--background-800" />}
      />

      {alerts.map((alert: IAlertMessage) => {
        return (
          <div className="pulse-mb-1" style={{ width: "100%" }} key={alert.id}>
            <AlertMessage
              id={alert.id}
              message={alert.message}
              status={alert.status}
              closeAlertMessage={closeAlert}
            />
          </div>
        );
      })}

      <div className="pulse-row open-orders-header-row open-orders-position-relative pulse-mb-1">
        <p className="pulse-typography-headline-6 pulse-color--background-50">
          Trading
        </p>
        <div className="balances-dropdown-inputs-container">
          <DropdownInput
            name="portfolio-select"
            value={context_state.selected_portfolio?.portfolio_id || ""}
            label="Portfolio"
            onChange={handleDropdownChange}
            disabled={context_state.org_metadata.portfolios.length < 1}
            options={mapIPortfoliosToSelectOptions(
              context_state.org_metadata.portfolios
            )}
            formInput={false}
            containerClassNames=""
          />

          <DropdownInput
            name="account-select"
            value={context_state.selected_account?.account_id || ""}
            label="Account"
            onChange={handleDropdownChange}
            disabled={
              context_state.selected_portfolio?.accounts
                ? context_state.selected_portfolio?.accounts.length < 1
                  ? true
                  : false
                : true
            }
            options={mapIAccountsToSelectOptions(
              context_state.selected_portfolio?.accounts || []
            )}
            formInput={false}
            containerClassNames=""
          />

          <AutocompleteInput
            value={symbolValue}
            inputLabel="Symbol"
            onChange={handleSymbolAutocompleteDropdownChange}
            disabled={symbolOptions.length === 0}
            options={memoizedSymbolOptions}
            labelInside={true}
            width={250}
            containerClassNames="pulse-ml-1"
          />
        </div>
      </div>

      <div className="pulse-row pulse-mb-1">
        <div
          className="pulse-color--background-800 pulse-justify-flex-end pulse-align-flex-end pulse-col-md-12 pulse-col-lg-12 pulse-col-xl-12"
          style={{
            flexDirection: "column",
          }}
        >
          <p
            className="pulse-typography-subtitle-2"
            style={{ color: "#b6bdce" }}
          >
            {`Last Market Data Update: ${
              bookLiveTimestamp
                ? `${dayjs(bookLiveTimestamp)
                    .utc()
                    .format("ddd, D MMM YYYY HH:mm:ss")} UTC`
                : "Pending..."
            }`}
          </p>

          <p
            className="pulse-typography-subtitle-2"
            style={{ color: "#b6bdce" }}
          >
            {`Last Drop Copy Update: ${
              orderLiveTimestamp
                ? `${dayjs(orderLiveTimestamp)
                    .utc()
                    .format("ddd, D MMM YYYY HH:mm:ss")} UTC`
                : "Pending..."
            }`}
          </p>
        </div>
      </div>

      <AGOrderBook gridRef={gridRef} rowData={rowData} loading={loading} />

      {loading && <Loading />}
    </div>
  );
};

export default TradingOverlay;
